import funnelConceptRegistry from "./funnelConceptRegistry.json";

const FUNNEL_CONCEPT_QUERY_PARAM = "concept";

export type FunnelConcept = {
  key: string;
  label: string;
  queryValues: string[];
  heroTitleLayout: "stacked" | "flow";
  heroTitleMain: string;
  heroTitleAccent: string;
  heroSubtitle: string;
};

type FunnelConceptRegistry = {
  schemaVersion: number;
  defaultKey: string;
  concepts: FunnelConcept[];
};

const registry = funnelConceptRegistry as FunnelConceptRegistry;
const conceptEntries = registry.concepts;

if (registry.schemaVersion !== 1 || conceptEntries.length === 0) {
  throw new Error("Invalid Linkhiv funnel concept registry.");
}

/**
 * Runtime map kept for existing consumers. The JSON registry is the canonical source shared
 * with paid-creative validation so a tagged campaign cannot name a concept the funnel ignores.
 */
export const FUNNEL_CONCEPTS = Object.fromEntries(
  conceptEntries.map((concept) => [concept.key, concept])
) as Record<string, FunnelConcept>;

export type FunnelConceptKey = FunnelConcept["key"];
const conceptByQueryValue = new Map(
  conceptEntries.flatMap((concept) =>
    concept.queryValues.map((value) => [value.toLowerCase(), concept] as const)
  )
);

export const DEFAULT_FUNNEL_CONCEPT =
  conceptEntries.find((concept) => concept.key === registry.defaultKey) ?? conceptEntries[0];

export const isFunnelConceptKey = (value: string): value is FunnelConceptKey =>
  conceptEntries.some((concept) => concept.key === value);

export const getFunnelConceptByKey = (key: FunnelConceptKey): FunnelConcept =>
  conceptEntries.find((concept) => concept.key === key) ?? DEFAULT_FUNNEL_CONCEPT;

export const getFunnelConceptFromValue = (value: string | null | undefined): FunnelConcept | null => {
  const normalized = value?.trim().toLowerCase();
  if (!normalized) return null;
  return conceptByQueryValue.get(normalized) ?? null;
};

export const getFunnelConceptFromLocation = (
  locationLike: Pick<Location, "href"> | URL
): FunnelConcept | null => {
  const url = locationLike instanceof URL ? locationLike : new URL(locationLike.href);
  return getFunnelConceptFromValue(url.searchParams.get(FUNNEL_CONCEPT_QUERY_PARAM));
};

export const getStoredOrDefaultFunnelConcept = (storedConceptKey: string | null | undefined): FunnelConcept =>
  storedConceptKey && isFunnelConceptKey(storedConceptKey)
    ? getFunnelConceptByKey(storedConceptKey)
    : DEFAULT_FUNNEL_CONCEPT;
